import {Util} from 'yougile-util'
import {Field} from './engine'
import {Action, Stage, WorkflowConfig} from './conf-interfaces'
import {Workflow, workflowToConfig} from './workflow'

export interface WorkflowIds {
  [id: string]: string /** path */
}

const pathToStr = (path: string[]): string => path.join('|')
const strToPath = (pathStr: string): string[] =>
  pathStr ? pathStr.split('|') : []

export const calculateWorkflowIds = (
  config: WorkflowConfig,
  prev: {config: WorkflowConfig; ids: WorkflowIds} | undefined
): WorkflowIds => {
  const result: WorkflowIds = {}

  // todo: use prev values to preserve ids after field renaming
  const ids = prev?.ids ?? {}

  const reuseOrCreateId = (...path: string[]): void => {
    const id = findIdInWorkflow(ids, ...path) ?? Util.longId()
    result[id] = pathToStr(path)
  }

  reuseOrCreateId('workflow')

  for (const stage of config.stages) {
    if (!stage.name) {
      continue
    }
    reuseOrCreateId('stages', stage.name)

    for (const action of stage.actions ?? []) {
      if (!action.name) {
        continue
      }
      reuseOrCreateId('stages', stage.name, action.name)
    }
  }

  for (const key of ['create', 'start'] as const) {
    for (const action of config[key]?.actions ?? []) {
      if (!action.name) {
        continue
      }
      reuseOrCreateId(key, action.name)
    }
  }

  for (const field of config.fields ?? []) {
    if (field.name) {
      reuseOrCreateId('fields', field.name)
    }
  }

  return result
}

export const updateWorkflowIds = (
  workflow: Workflow,
  old: Workflow | undefined
): void => {
  const oldConf = old ? workflowToConfig(old) : undefined
  const newConf = workflowToConfig(workflow)
  workflow.ids = calculateWorkflowIds(
    newConf,
    old && oldConf ? {config: oldConf, ids: old?.ids} : undefined
  )
}

export const findIdInWorkflow = (
  ids: WorkflowIds,
  ...path: string[]
): string | undefined => {
  for (const id of Object.keys(ids)) {
    if (ids[id] === pathToStr(path)) {
      return id
    }
  }
  return undefined
}

export const getWorkflowFieldById = (
  config: WorkflowConfig,
  ids: WorkflowIds,
  id: string | undefined
): Field | undefined => {
  if (!id) {
    return undefined
  }
  const path = strToPath(ids[id])
  if (path.length !== 2) {
    return undefined
  }
  return config?.fields?.find(f => f.name === path[1])
}

export const getWorkflowActionById = (
  config: WorkflowConfig,
  ids: WorkflowIds,
  id: string | undefined
): {stage?: Stage; action?: Action} => {
  if (!id) {
    return {}
  }
  const path = strToPath(ids[id])

  const fromStage = (
    name: string,
    stage: Stage | undefined
  ): {stage?: Stage; action?: Action} => {
    const action = stage?.actions?.find(a => a.name === name)
    return {stage, action}
  }

  if (path[0] === 'stages' && path.length === 3) {
    const [, stageName, actionName] = path
    const stage = config.stages?.find(s => s.name === stageName)
    return fromStage(actionName, stage)
  }

  for (const key of ['create', 'start'] as const) {
    if (path[0] === key && path.length === 2) {
      return fromStage(path[1], {...config[key], name: `special:${key}`})
    }
  }

  return {}
}

export const getWorkflowAction = (
  workflow: Workflow,
  id: string | undefined
): {stage?: Stage; action?: Action} => {
  return getWorkflowActionById(workflowToConfig(workflow), workflow.ids, id)
}

export const getWorkflowStageById = (
  config: WorkflowConfig,
  ids: WorkflowIds,
  id: string | undefined
): Stage | undefined => {
  if (!id) {
    return undefined
  }
  const path = strToPath(ids[id])

  if (path[0] === 'stages' && path.length === 2) {
    const [, stageName] = path
    return config.stages?.find(s => s.name === stageName)
  }

  return undefined
}

export const getWorkflowStage = (
  workflow: Workflow,
  id: string | undefined
): Stage | undefined => {
  return getWorkflowStageById(workflowToConfig(workflow), workflow.ids, id)
}
